import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Header from "../../components/header/Header";
import { toast } from "react-hot-toast";
import Sidebar from "../../components/sidebar/Sidebar";
import "../account/SubscriptionSection.css";
import api from "../../utils/api";
import SubscriptionManagementSkeleton from "../../components/skeletons/SubscriptionManagementSkeleton";

const SubscriptionManagement = () => {
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // Replace the mock useEffect with this:
  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const res = await api.get(`/subscription/current`);
        if (res.data.success && res.data.subscription) {
          setSubscription(res.data.subscription);
        } else {
          setSubscription(null);
        }
      } catch (error) {
        console.error("Failed to fetch subscription:", error);
        setSubscription(null);
      } finally {
        setLoading(false);
      }
    };

    fetchSubscription();
  }, []);

  const handleCancelSubscription = async () => {
    if (!subscription?.subscriptionCode || !subscription?.emailToken) {
      toast.error("Cannot cancel subscription. Missing required details.");
      return;
    }

    setCancelLoading(true);

    try {
      const response = await api.post(`/subscription/cancel`, {
        subscriptionCode: subscription.subscriptionCode,
        emailToken: subscription.emailToken,
      });
      if (response.data.success) {
        toast.success("Your subscription has been cancelled successfully.");

        // Update local state
        setSubscription((prev) => ({
          ...prev,
          status: "cancelled",
          cancelledAt: new Date().toISOString(),
        }));

        setShowCancelConfirm(false);
      } else {
        toast.error("Failed to cancel subscription.");
      }
    } catch (error) {
      console.error("Cancel error:", error);
      toast.error("Failed to cancel subscription. Please try again.");
    } finally {
      setCancelLoading(false);
    }
  };

  if (loading) return <SubscriptionManagementSkeleton />;

  if (!subscription || subscription.status !== "active") {
    return (
      <div className="sub-mg-dashboard-layout">
        <Header onMenuToggle={() => setSidebarOpen(!sidebarOpen)} />
        <Sidebar
          isOpen={sidebarOpen}
          onToggle={() => setSidebarOpen(!sidebarOpen)}
        />
        <div className="dashboard-content subscription-management">
          <h1 className="subscription-page-title">Subscription Management</h1>
          <div className="sub-mg-no-subscription">
            <h3>You currently have no active subscription.</h3>
            <p>Subscribe to unlock premium features.</p>
            <button
              className="sub-mg-subscribe-now-btn"
              onClick={() => navigate("/subscriptions")}
            >
              Browse Plans
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-layout">
      <Header onMenuToggle={() => setSidebarOpen(!sidebarOpen)} />
      <Sidebar
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
      />

      <div className="dashboard-content subscription-management">
        <h1 className="subscription-page-title">Subscription Management</h1>
        <p className="sub-page-subtitle">Manage your current subscription</p>

        <div className="sub-mg-subscription-card">
          <div className="sub-mg-subscription-header">
            <div className="sub-mg-plan-info">
              <span className="sub-mg-plan-badge">{subscription.plan}</span>
              <h2>
                ₦{subscription.amount.toLocaleString()}/
                {subscription.billingCycle}
              </h2>
            </div>
            <div className={`sub-mg-status-badge ${subscription.status}`}>
              {subscription.status.toUpperCase()}
            </div>
          </div>

          <div className="sub-mg-subscription-details">
            <div className="sub-mg-detail-row">
              <span className="label">Started on</span>
              <span className="sub-mg-value">
                {new Date(subscription.startDate).toLocaleDateString("en-GB")}
              </span>
            </div>
            <div className="sub-mg-detail-row">
              <span className="label">Next billing date</span>
              <span className="sub-mg-value">
                {new Date(subscription.nextBillingDate).toLocaleDateString(
                  "en-GB",
                )}
              </span>
            </div>
            <div className="sub-mg-detail-row">
              <span className="label">Billing cycle</span>
              <span className="sub-mg-value">{subscription.billingCycle}</span>
            </div>
          </div>

          <div className="sub-mg-action-section">
            <button
              className="sub-mg-cancel-subscription-btn"
              onClick={() => setShowCancelConfirm(true)}
              disabled={cancelLoading}
            >
              {cancelLoading ? "Cancelling..." : "Cancel Subscription"}
            </button>

            <p className="sub-mg-cancel-note">
              You can cancel anytime. Your subscription will remain active until
              the end of the current billing period.
            </p>
          </div>
        </div>
      </div>

      {/* Cancel Confirmation Modal */}
      {showCancelConfirm && (
        <div
          className="modal-overlay"
          onClick={() => setShowCancelConfirm(false)}
        >
          <div
            className="cancel-confirm-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Cancel Subscription?</h3>
            <p>
              Are you sure you want to cancel your{" "}
              <strong>{subscription.plan}</strong> subscription?
              <br />
              You will lose access to premium features after{" "}
              {new Date(subscription.nextBillingDate).toLocaleDateString(
                "en-GB",
              )}
              .
            </p>

            <div className="confirm-actions">
              <button
                className="cancel-btn"
                onClick={() => setShowCancelConfirm(false)}
              >
                No, Keep Subscription
              </button>
              <button
                className="confirm-cancel-btn"
                onClick={handleCancelSubscription}
                disabled={cancelLoading}
              >
                {cancelLoading ? "Cancelling..." : "Yes, Cancel Subscription"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionManagement;
